/**
 * FavouriteButton — heart toggle for events + civilian nights.
 *
 * Props:
 *   kind: 'event' | 'civilian'
 *   id:   string  (event_id or civ_id)
 *   size: 'sm' | 'md'
 *   className?
 *
 * Behaviour:
 *   - Signed-in user → optimistic toggle via /api/favourites/...
 *   - Guest          → fires the shared AuthGateDialog asking them to
 *                       register / log in.
 *
 * Always renders the icon so the button keeps the same footprint
 * regardless of auth state (per product spec: guests SEE the icon
 * but get gated on tap).
 */
import { useState } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import useFavouritesCtx from '@/hooks/useFavourites';
import AuthGateDialog from '@/components/AuthGateDialog';

export default function FavouriteButton({ kind = 'event', id, size = 'md', className = '' }) {
  const { user } = useAuth();
  const { isFavourite, toggle } = useFavouritesCtx();
  const [busy, setBusy] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  const fav = !!user && isFavourite(kind, id);
  const dim = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const padding = size === 'sm' ? 'p-1.5' : 'p-2';

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      setGateOpen(true);
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const nowFav = await toggle(kind, id);
      if (nowFav) toast.success('Added to favourites');
      else toast('Removed from favourites');
    } catch {
      toast.error('Could not update favourites');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-label={fav ? 'Remove from favourites' : 'Add to favourites'}
        data-testid={`favourite-btn-${kind}-${id}`}
        className={`inline-flex items-center justify-center rounded-full ${padding} border transition-colors ${
          fav
            ? 'bg-rose-500/15 border-rose-500/50 text-rose-500'
            : 'bg-background/70 border-border text-muted-foreground hover:text-rose-400 hover:border-rose-400/50'
        } ${className}`}
      >
        {busy ? (
          <Loader2 className={`${dim} animate-spin`} />
        ) : (
          <Heart className={dim} fill={fav ? 'currentColor' : 'none'} />
        )}
      </button>

      <AuthGateDialog
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        feature="favourite events"
      />
    </>
  );
}
